import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { ArticlesSection } from '../components/Article/ArticlesSection';
import ArticlesSectionSkeleton from '../components/Article/ArticlesSectionSkeleton';
import { NoResults } from '../components/ContentSearch/NoResults';
import SearchInput from '../components/ContentSearch/SearchInput';
import PredictiveSearchResultsList from '../components/ContentSearch/PredictiveSearchResultsList';
import {
  getFormattedSearchValue,
  minSearchLength,
} from '../components/ContentSearch/SearchUtils';
import OpenGraphGeneral from '../components/MetaData/OpenGraphGeneral';
import PageHeader from '../components/Page/PageHeader';
import useDocumentTitle from '../hooks/useDocumentTitle';
import usePredictiveSearch from '../hooks/usePredictiveSearch';
import { addEventToGTMDataLayer, articleCardLocation } from '../utilities/gtm';
import { useSearchArticles } from '../hooks/useSearchArticles';
import { useDebounce } from '../hooks/useDebounce';
import { debounceDelay } from '../components/ContentSearch/constants';
import { ContentType } from '../types/types';
import ContentTypeSelect from '../components/Article/ContentTypeSelect';

const ITEMS_PER_PAGE = 10;

export function SearchPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('search.title'));

  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));

  const [searchParams, setSearchParams] = useSearchParams();
  const [inputValue, setInputValue] = useState(searchParams.get('value') ?? '');
  const debouncedSearch = useDebounce(inputValue, debounceDelay);

  // Multi-select: selectedContentTypes is an array of ContentType
  const [selectedContentTypes, setSelectedContentTypes] = useState<
    ContentType[]
  >(() => {
    const param = searchParams.get('contentType');
    if (param) {
      return param.split(',') as ContentType[];
    }
    return [];
  });

  const [showError, setShowError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Add predictive search functionality
  const { matchingAuthors, matchingCategories } = usePredictiveSearch(
    getFormattedSearchValue(inputValue),
  );
  const showPredictiveResults =
    inputValue.length >= minSearchLength &&
    (matchingAuthors.length > 0 || matchingCategories.length > 0);

  const updateSearchParams = useCallback(
    (search: string, contentTypes: ContentType[]) => {
      setSearchParams({
        value: search,
        ...(contentTypes.length > 0
          ? { contentType: contentTypes.join(',') }
          : {}),
      });
    },
    [setSearchParams],
  );

  const {
    articles,
    totalArticleCount,
    fetchNextPage,
    hasNextPage,
    isFetching,
    status,
  } = useSearchArticles({
    minSearchLength,
    search: debouncedSearch,
    itemsPerPage: ITEMS_PER_PAGE,
    contentTypes: selectedContentTypes,
  });

  const performSearch = useCallback(
    (search: string, contentTypes: ContentType[]) => {
      if (search.length < minSearchLength) {
        setShowError(true);
        return;
      }
      setShowError(false);
      setInputValue(search);
      updateSearchParams(search, contentTypes);
    },
    [updateSearchParams],
  );

  const handleSubmit = useCallback(
    (search: string) => {
      performSearch(search, selectedContentTypes);
    },
    [performSearch, selectedContentTypes],
  );

  const handleChange = useCallback((search: string) => {
    setShowError(false);
    setInputValue(search);
  }, []);

  const handleContentTypeChange = useCallback(
    (value: ContentType[]) => {
      setSelectedContentTypes(value);
      performSearch(inputValue, value);
    },
    [inputValue, performSearch],
  );

  const handleAnalytics = useCallback(
    (count: number) => {
      addEventToGTMDataLayer({
        event: 'search',
        search_term: inputValue,
        formatted_search_term: getFormattedSearchValue(inputValue),
        search_results_count: count,
      });
    },
    [inputValue],
  );

  useEffect(() => {
    if (totalArticleCount > 0) {
      handleAnalytics(totalArticleCount);
    }
  }, [totalArticleCount, handleAnalytics]);

  // Effect to handle search from the URL parameters
  useEffect(() => {
    const urlValue = searchParams.get('value') ?? '';
    const urlContentType = searchParams.get('contentType') ?? '';
    let types: ContentType[] = [];
    if (urlContentType) {
      types = urlContentType.split(',') as ContentType[];
    }
    setInputValue(urlValue);
    setSelectedContentTypes(types);
    if (inputRef.current) {
      inputRef.current.value = urlValue;
    }
    if (urlValue && urlValue.length >= minSearchLength) {
      performSearch(urlValue, types);
    }
  }, [searchParams, performSearch]);

  return (
    <Box>
      <PageHeader title={t('search.title')} />
      <Grid container>
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <Paper
            variant='outlined'
            component='form'
            noValidate
            autoComplete='off'
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(inputRef.current?.value ?? '');
            }}
            sx={{ padding: 2, paddingTop: 3 }}
          >
            <Grid container spacing={2}>
              {/* Search input - full width on mobile, 6/12 on desktop */}
              <Grid size={{ xs: 12, md: 5 }}>
                <SearchInput
                  id='advanced-content-search'
                  inputRef={inputRef}
                  onChange={handleChange}
                  onSubmit={handleSubmit}
                  dense={!isLargeScreen}
                  error={showError}
                  helperText={
                    showError
                      ? t('search.searchTerm.minCharacters', {
                          count: minSearchLength,
                        })
                      : t('search.searchTerm.helperText')
                  }
                />
              </Grid>

              {/* Content type filter - full width on mobile, 4/12 on desktop */}
              <Grid size={{ xs: 12, md: 5 }}>
                <ContentTypeSelect
                  value={selectedContentTypes}
                  onChange={handleContentTypeChange}
                  size={isLargeScreen ? 'medium' : 'small'}
                />
              </Grid>

              {/* Search button - full width on mobile, 2/12 on desktop */}
              <Grid size={{ xs: 12, md: 2 }}>
                <Button
                  variant='contained'
                  endIcon={<SearchIcon />}
                  onClick={() => handleSubmit(inputRef.current?.value ?? '')}
                  size={isLargeScreen ? 'large' : 'medium'}
                  fullWidth
                  sx={{
                    height: isLargeScreen ? '56px' : '40px',
                    minHeight: isLargeScreen ? '56px' : '40px',
                  }}
                  disabled={isFetching}
                >
                  {t('search.submit')}
                </Button>
              </Grid>
            </Grid>
          </Paper>
          <Box
            sx={{
              marginTop: 6,
            }}
          >
            {/* Two-column layout for search results */}
            <Grid container spacing={6}>
              {/* Main search results column */}
              <Grid size={{ xs: 12, md: showPredictiveResults ? 8 : 12 }}>
                {isFetching && articles.length === 0 && (
                  <>
                    <Stack
                      direction='row'
                      useFlexGap
                      sx={{
                        paddingLeft: 2,
                        paddingBottom: 2,
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <Typography variant='h2'>
                        {t('search.content.searching')}
                      </Typography>
                      <CircularProgress size='1rem' />
                    </Stack>
                    <ArticlesSectionSkeleton />
                  </>
                )}
                {status === 'success' && articles.length === 0 && <NoResults />}
                {articles.length > 0 && (
                  <Box>
                    <Stack
                      direction='row'
                      useFlexGap
                      sx={{
                        paddingBottom: 2,
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <Typography variant='h2'>
                        {t('search.content.results')}
                      </Typography>
                    </Stack>
                    <ArticlesSection
                      articles={articles}
                      headingLevel='h3'
                      location={articleCardLocation.MAIN_CONTENT}
                      hasNextPage={hasNextPage}
                      showArticleAmount
                      totalArticleCount={totalArticleCount}
                      fetchNextPage={fetchNextPage}
                    />
                  </Box>
                )}
              </Grid>

              {/* Predictive results sidebar - only show on larger screens when there are results */}
              {showPredictiveResults && isLargeScreen && (
                <Grid size={{ md: 4 }}>
                  <Box
                    sx={{
                      position: 'sticky',
                      top: 2,
                      paddingLeft: 2,
                      paddingTop: 4,
                    }}
                  >
                    <PredictiveSearchResultsList
                      authors={matchingAuthors}
                      categories={matchingCategories}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>

            {/* Show predictive results on top for smaller screens */}
            {showPredictiveResults && !isLargeScreen && (
              <Box sx={{ marginBottom: 4 }}>
                <PredictiveSearchResultsList
                  authors={matchingAuthors}
                  categories={matchingCategories}
                />
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
      <OpenGraphGeneral />
    </Box>
  );
}
